import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useInventory } from '../hooks/useInventory';
import CommonPageHeader from '../components/common/CommonPageHeader';
import StockOverview, { StockAction } from '../components/warehouse/StockOverview';
import InventoryMovementsList from '../components/warehouse/InventoryMovementsList';
import InventoryDocuments from '../components/warehouse/InventoryDocuments';
import WarehouseList from '../components/warehouse/WarehouseList';
import StockCounts from '../components/warehouse/StockCounts';
import StockTransferForm from '../components/warehouse/StockTransferForm';
import StockReceiptForm from '../components/warehouse/StockReceiptForm';
import StockMovementModal, { StockMovementMode } from '../components/warehouse/StockMovementModal';
import {
  ArchiveBoxIcon, ArrowDownOnSquareIcon, ArrowUpTrayIcon, ArrowsRightLeftIcon, TrashIcon, CubeIcon, ClockIcon,
  DocumentTextIcon, ClipboardDocumentCheckIcon, BuildingStorefrontIcon, PlusIcon, ArrowPathIcon
} from '@heroicons/react/24/outline';

type TabId = 'stock' | 'movements' | 'documents' | 'counts' | 'warehouses';
const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'stock', label: 'Készlet', icon: CubeIcon },
  { id: 'movements', label: 'Mozgások', icon: ClockIcon },
  { id: 'documents', label: 'Bizonylatok', icon: DocumentTextIcon },
  { id: 'counts', label: 'Leltár', icon: ClipboardDocumentCheckIcon },
  { id: 'warehouses', label: 'Raktárak', icon: BuildingStorefrontIcon }
];

/** Raktár – egy oldal: készlet, mozgások, bizonylatok, leltár, raktárak. */
const Inventory: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user as any)?.role === 'admin' || (user as any)?.role === 'super_admin';
  const { recalculateProductTotals } = useInventory();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find(t => t.id === params.get('tab'))?.id || 'stock') as TabId;
  const setTab = (id: TabId) => setParams(prev => { const n = new URLSearchParams(prev); n.set('tab', id); return n; }, { replace: true });

  const [receipt, setReceipt] = useState<{ open: boolean; warehouseId?: string }>({ open: false });
  const [transfer, setTransfer] = useState<{ open: boolean; productId?: string; warehouseId?: string }>({ open: false });
  const [movement, setMovement] = useState<{ open: boolean; mode: StockMovementMode; productId?: string; warehouseId?: string }>({ open: false, mode: 'out' });
  const [recalcBusy, setRecalcBusy] = useState(false);

  const onAction = (a: StockAction, ctx: { productId?: string; warehouseId?: string }) => {
    if (a === 'in') setReceipt({ open: true, warehouseId: ctx.warehouseId });
    else if (a === 'transfer') setTransfer({ open: true, productId: ctx.productId, warehouseId: ctx.warehouseId });
    else setMovement({ open: true, mode: a === 'scrap' ? 'scrap' : a === 'adjust' ? 'adjust' : 'out', productId: ctx.productId, warehouseId: ctx.warehouseId });
  };

  const recalc = async () => {
    setRecalcBusy(true);
    try { const n = await recalculateProductTotals(); toast.success(`${n} termék összkészlete újraszámolva`); }
    catch (e: any) { toast.error(e?.message || 'Nem sikerült'); }
    finally { setRecalcBusy(false); }
  };

  return (
    <div className="w-full max-w-full space-y-3">
      <CommonPageHeader title="Raktár" subtitle="Készlet, mozgások, leltár" icon={ArchiveBoxIcon}>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button type="button" onClick={() => setMovement({ open: true, mode: 'scrap' })} className="crm-btn crm-btn-secondary h-8 text-xs"><TrashIcon className="w-4 h-4" /><span className="hidden md:inline">Selejt</span></button>
          <button type="button" onClick={() => setTransfer({ open: true })} className="crm-btn crm-btn-secondary h-8 text-xs"><ArrowsRightLeftIcon className="w-4 h-4" /><span className="hidden md:inline">Átmozgatás</span></button>
          <button type="button" onClick={() => setMovement({ open: true, mode: 'out' })} className="crm-btn crm-btn-secondary h-8 text-xs"><ArrowUpTrayIcon className="w-4 h-4" /><span className="hidden sm:inline">Kiadás</span></button>
          <button type="button" onClick={() => setReceipt({ open: true })} className="crm-btn crm-btn-primary h-8 text-xs"><ArrowDownOnSquareIcon className="w-4 h-4" />Bevételezés</button>
        </div>
      </CommonPageHeader>

      <div className="px-3 sm:px-4 md:px-5 space-y-3">
        <div className="flex items-center rounded-lg border border-[var(--crm-border)] bg-[var(--crm-surface)] p-0.5 overflow-x-auto w-full sm:w-auto sm:inline-flex">
          {TABS.map(t => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap ${tab === t.id ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>

        {tab === 'stock' && <StockOverview onAction={onAction} onOpenProduct={(id) => navigate(`/products?product=${id}`)} />}
        {tab === 'movements' && <div className="crm-card overflow-hidden"><InventoryMovementsList /></div>}
        {tab === 'documents' && <div className="crm-card overflow-hidden"><InventoryDocuments /></div>}
        {tab === 'counts' && <StockCounts />}
        {tab === 'warehouses' && (
          <div className="space-y-3">
            <div className="crm-card p-3 flex flex-wrap items-center gap-2">
              <p className="text-xs text-gray-500 dark:text-gray-400 flex-1">Raktárak és raktárhelyek. Az alapértelmezett raktárt ajánlja fel a kiadás, a munkalap anyaglevonás és a leltár.</p>
              {isAdmin && <button type="button" onClick={recalc} disabled={recalcBusy} className="crm-btn crm-btn-ghost h-8 text-xs" title="Ha valahol nem egyezik a termék összkészlete a raktárak összegével"><ArrowPathIcon className={`w-4 h-4 ${recalcBusy ? 'animate-spin' : ''}`} />Összkészlet újraszámolása</button>}
              <button type="button" onClick={() => (window as any).triggerCreateWarehouse?.()} className="crm-btn crm-btn-primary h-8 text-xs"><PlusIcon className="w-4 h-4" />Új raktár</button>
            </div>
            <div className="crm-card overflow-hidden"><WarehouseList onCreateWarehouse={() => {}} /></div>
          </div>
        )}
      </div>

      <StockReceiptForm isOpen={receipt.open} onClose={() => setReceipt({ open: false })} preSelectedWarehouseId={receipt.warehouseId} />
      <StockTransferForm isOpen={transfer.open} onClose={() => setTransfer({ open: false })} preSelectedProductId={transfer.productId} preSelectedFromWarehouseId={transfer.warehouseId} />
      <StockMovementModal isOpen={movement.open} mode={movement.mode} onClose={() => setMovement(m => ({ ...m, open: false }))} preProductId={movement.productId} preWarehouseId={movement.warehouseId} />
    </div>
  );
};

export default Inventory;
